/** Backup and recovery: uploaded, archived and verified shown apart; restore epoch gates live readiness (U086-U090). */
import { decodeRecoveryRow, type RecoveryRow } from "../../contracts/ops.ts";
import { badge, definitions, instantCell, metric } from "../../app/components.ts";
import { h, type Child } from "../../app/dom.ts";
import { formatDurationUs } from "../../app/format.ts";
import type { ViewContext, ViewHandle } from "../../app/view.ts";
import { byKindOf, lagUs, mountOps, panel, simpleTable } from "../ops-common.ts";

type Backup = Extract<RecoveryRow, { kind: "backup" }>;
type RestoreEpoch = Extract<RecoveryRow, { kind: "restore_epoch" }>;

/** What the backup can honestly promise: the verified point, and the unverified tail beyond it. */
export function recoverablePoint(row: Backup): {
  readonly text: string;
  readonly unverifiedUs: number | null;
} {
  const { uploadedThrough, verifiedThrough } = row;
  if (uploadedThrough === null || verifiedThrough === null) {
    return {
      text: "No restore has verified this backup: it is uploaded, not known recoverable.",
      unverifiedUs: null,
    };
  }
  const tail = lagUs(verifiedThrough, uploadedThrough);
  return {
    text:
      tail === 0
        ? "The verified point equals the uploaded point."
        : `Uploaded data after the verified point (${formatDurationUs(tail)}) is not verified.`,
    unverifiedUs: tail,
  };
}

function target(measured: number | null, targetUs: number): Child {
  if (measured === null) {
    return metric(null);
  }
  return h(
    "span",
    {},
    `${formatDurationUs(measured)} of ${formatDurationUs(targetUs)} `,
    badge(
      measured <= targetUs ? "within target" : "over target",
      measured <= targetUs ? "ok" : "bad",
    ),
  );
}

function backupPanel(row: Backup): HTMLElement {
  const point = recoverablePoint(row);
  return panel(
    "Backup: uploaded, archived and verified",
    "recovery-backup",
    h(
      "div",
      { class: "separate-status" },
      h(
        "div",
        { attrs: { "data-status": "uploaded" } },
        h("h3", {}, "Uploaded"),
        h("p", {}, "Through ", instantCell(row.uploadedThrough)),
        h("p", {}, "Location: ", row.locationClass),
      ),
      h(
        "div",
        { attrs: { "data-status": "archive" } },
        h("h3", {}, "Archive continuity"),
        h(
          "p",
          {},
          badge(row.archiveContinuity, row.archiveContinuity === "CONTINUOUS" ? "ok" : "bad"),
        ),
        h(
          "p",
          {},
          "Archive lag: ",
          metric(row.archiveLagUs === null ? null : formatDurationUs(row.archiveLagUs)),
        ),
      ),
      h(
        "div",
        { attrs: { "data-status": "verified" } },
        h("h3", {}, "Verified by restore"),
        h("p", {}, "Through ", instantCell(row.verifiedThrough)),
        h("p", {}, "Drill: ", metric(row.lastVerifiedDrill)),
      ),
    ),
    h(
      "p",
      { attrs: { role: "status", "data-recoverable": point.unverifiedUs === 0 ? "equal" : "gap" } },
      point.text,
    ),
    definitions([
      ["RPO", target(row.rpoMeasuredUs, row.rpoTargetUs)],
      ["RTO", target(row.rtoMeasuredUs, row.rtoTargetUs)],
      ["Key recovery", row.keyRecoveryReady ? "ready (metadata only, no key shown)" : "not ready"],
    ]),
  );
}

function epochPanel(row: RestoreEpoch): HTMLElement {
  return panel(
    "Restore epoch",
    "recovery-epoch",
    definitions([
      ["Epoch", String(row.epoch)],
      ["Restored", instantCell(row.restoredAt)],
      [
        "Reconciliation",
        badge(row.reconciliation, row.reconciliation === "PASSED" ? "ok" : "warn"),
      ],
      ["Runbook", row.runbookRef],
      ["Accountable owner", metric(row.incidentOwner)],
    ]),
    h(
      "p",
      {
        attrs: {
          role: row.liveReady ? "status" : "alert",
          "data-live-ready": String(row.liveReady),
        },
      },
      row.liveReady
        ? "Live-ready: the reconciliation passed."
        : `Not live-ready: reconciliation is ${row.reconciliation.replace("_", " ").toLowerCase()}. Recovery progress is not live readiness.`,
    ),
  );
}

/** Draw the backup, the drills and the restore epoch. */
export function drawRecovery(rows: readonly RecoveryRow[]): readonly Child[] {
  return [
    ...byKindOf(rows, "backup").map(backupPanel),
    panel(
      "Restore drills",
      "recovery-drills",
      simpleTable(
        "Restore drills",
        [
          "Drill",
          "When",
          "Result",
          "Missing segments",
          "Corruption findings",
          "Open follow-ups",
          "Summary",
        ],
        byKindOf(rows, "drill").map((row) => [
          row.drillId,
          instantCell(row.at),
          badge(row.result, row.result === "PASSED" ? "ok" : "bad"),
          String(row.missingSegments),
          String(row.corruptionFindings),
          String(row.openFollowups),
          row.summary,
        ]),
      ),
    ),
    ...byKindOf(rows, "restore_epoch").map(epochPanel),
  ];
}

/** Mount the recovery view. */
export function mountRecovery(ctx: ViewContext): ViewHandle {
  return mountOps(ctx, {
    id: "recovery",
    title: "Backup and recovery",
    projection: "recovery",
    row: decodeRecoveryRow,
    draw: (rows) => drawRecovery(rows),
  });
}
